import { ProfileIcon } from "@/components/icons/profile-icon";
import { useAvatar } from "@/hooks/requests/user/use-avatar";
import { useUploadAvatar } from "@/hooks/requests/user/use-upload-avatar";
import { useToast } from "@/hooks/ui/use-toast";
import { cn } from "@/utils/cn";
import { ChangeEvent, useRef } from "react";

interface ProfileImageProps {
  className?: string;
}

export const ProfileImage = function ProfileImage({ className }: ProfileImageProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { avatarImageUrl } = useAvatar();
  const { uploadAvatarAsync, isUploading } = useUploadAvatar();
  const { showError } = useToast();

  const handleSelectFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      await uploadAvatarAsync(file);
    } catch (error) {
      if (error instanceof Error) {
        showError(error.message);
      } else {
        showError("Failed to upload avatar.");
      }
    } finally {
      event.target.value = "";
    }
  };

  const handlePickAvatar = () => {
    if (isUploading) return;
    fileInputRef.current?.click();
  };

  return (
    <div className={cn("relative inline-flex", className)}>
      <button
        type="button"
        className={cn(
          "relative flex h-28 w-28 items-center justify-center overflow-hidden rounded-full",
          "z-10 appearance-none border-0 p-0",
          avatarImageUrl
            ? "bg-gray-theme border-2 border-white shadow-[inset_0_4px_11px_0_rgba(0,0,0,0.25)]"
            : "border border-[#E3CDFF] bg-[#F7F2FF] shadow-[inset_0px_4px_11px_rgba(98,10,213,0.12)]",
        )}
        onClick={handlePickAvatar}
        disabled={isUploading}
        aria-busy={isUploading}
        aria-label="Upload avatar"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleSelectFile}
          className="hidden"
        />
        {!!avatarImageUrl ? (
          <>
            <img src={avatarImageUrl} alt="Profile image" className="h-full w-full object-cover" />
            <span className="pointer-events-none absolute inset-0 rounded-full shadow-[inset_0_8px_18px_rgba(0,0,0,0.45)]" />
          </>
        ) : (
          <ProfileIcon className="text-os-brand h-8 w-8" />
        )}
      </button>

      <button
        type="button"
        onClick={handlePickAvatar}
        disabled={isUploading}
        aria-label={avatarImageUrl ? "Edit avatar" : "Add avatar"}
        className="bg-os-brand absolute top-0 right-0 z-20 flex h-8 w-8 items-center justify-center rounded-full transition-opacity active:opacity-70"
      >
        {avatarImageUrl ? (
          <svg
            viewBox="0 0 12 12"
            className="h-4 w-4"
            fill="none"
            stroke="white"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M8.5 1.5L10.5 3.5L4 10L1.5 10.5L2 8L8.5 1.5Z" />
            <path d="M7 3L9 5" />
          </svg>
        ) : (
          <svg
            viewBox="0 0 10 10"
            className="h-3.5 w-3.5"
            fill="none"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
          >
            <path d="M5 1v8M1 5h8" />
          </svg>
        )}
      </button>
    </div>
  );
};
